import { useAppSelector } from 'redux/store/store.types';
import { EEecsRegistry, ISelectOption } from 'types';

import { EGrexCountry, GrexCountryMap } from 'modules/grex';
import { INecsAccount } from 'modules/necs';

import { useLazyGetEnergyTypesQuery } from '../api/eecs-common.api';
import { EnergyTypesResponse } from '../types/eecs-common.types';

interface UseEecsCommon {
  isHasRegistryAccount: boolean;
  isHasEecsAccount: boolean;
  eecsAccount: INecsAccount | null | undefined;
  registryOptions: ISelectOption[];
  defaultRegistryOptionKey: string | number | undefined;
  getEnergyTypesHandler: () => Promise<EnergyTypesResponse | null>;
}

export const useEecsCommon = (): UseEecsCommon => {
  const { account: necsAccount } = useAppSelector((state) => state.necsAccount);
  const { account: hknrAccount } = useAppSelector((state) => state.hknrAccount);
  const { firstAccount: grexAccount, accounts: grexAccounts } = useAppSelector((state) => state.grexAccounts);

  const isHasRegistryAccount = !!necsAccount || !!hknrAccount || !!grexAccount;
  const isHasEecsAccount = !!necsAccount || !!grexAccount;
  const eecsAccount = necsAccount || grexAccount;

  let registryOptions: ISelectOption[] = [];

  if (necsAccount) {
    registryOptions.push({ id: EEecsRegistry.NECS, value: EEecsRegistry.NECS, name: 'NECS', label: 'NECS' });
  }

  if (hknrAccount) {
    registryOptions.push({ id: EEecsRegistry.HKNR, value: EEecsRegistry.HKNR, name: 'HKNR', label: 'HKNR' });
  }

  Object.keys(grexAccounts).forEach((key) => {
    const account = grexAccounts[key as EGrexCountry];
    if (account) {
      registryOptions.push({
        id: GrexCountryMap[key as EGrexCountry].registry,
        value: GrexCountryMap[key as EGrexCountry].registry,
        name: GrexCountryMap[key as EGrexCountry].shortName,
        label: GrexCountryMap[key as EGrexCountry].shortName,
      });
    }
  });

  registryOptions = registryOptions.sort((a, b) => a?.name?.localeCompare(b?.name || '') || 0);
  const defaultRegistryOptionKey = registryOptions[0]?.id;

  const [getEnergyTypes] = useLazyGetEnergyTypesQuery();

  const getEnergyTypesHandler = async () => {
    const { data: response } = await getEnergyTypes();
    return response?.data || null;
  };

  return {
    getEnergyTypesHandler,
    isHasRegistryAccount,
    isHasEecsAccount,
    eecsAccount,
    registryOptions,
    defaultRegistryOptionKey,
  };
};
